import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { Link } from "react-router-dom";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useOutletContext } from "react-router-dom";
import MpgInput from "../../../../../CommonFormComp/MpgInput";
import MpgFileUpload from "../../../../../fileHandling/MpgFileUpload";
import MpgAutocompleteMulti from "../../../../../CommonFormComp/MpgAutocompleteMulti";
import MpgAutocompleteSingle from "../../../../../CommonFormComp/MpgAutocompleteSingle";
import {
  useGetLanguageListQuery,
  useGetAccountAdminListQuery,
  useFileDownloadMutation,
  useGetCurrencyListQuery,
  useGetClientTypeClassificationQuery,
} from "../../../../../reactQueries/commonRQHooks";
import { useViewAccountTeamMembersListQuery } from "../../../../../reactQueries/AppApi/rqHooksForAccountTeamMembers";
import CircularProgress from "@mui/material/CircularProgress";
import { createClientSubAccValidation } from "./createClientSubAccValidation";
import ClientTeamTable from "../../accountTableComp/ClientTeamTable";
import AccountAdminTable from "../../accountTableComp/AccountAdminTable";
import AccountTeamTable from "../../accountTableComp/AccountTeamTable";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import Tooltip from "@mui/material/Tooltip";

import AccountAdminsDialog from "../../AccountAdminsDialog";
import AccountTeamMemberDialog from "../../AccountTeamMemberDialog";

import {
  useGetClientAccountDropdownMutation,
  useGetLanguageListByClientAccountOrClientSubAccountId,
} from "../../../../../reactQueries/AppApi/rqHooksForClientUsers";

import {
  useGetClientCodeListByClientAccountIdQuery,
  useCreateClientSubAccountMutation,
  useUploadLogoOfClientSubAccountMutation,
} from "../../../../../reactQueries/AppApi/rqHooksForClientSubAccount";
import Stack from "@mui/material/Stack";
import { clientSubAccountViewPath } from "../../../../../routes/routePaths";
import MpgErrorDialog from "../../../../../common/dialog/MpgErrorDialog";
import { useGetClientAccountByIdQuery } from "../../../../../reactQueries/AppApi/rqHooksForClientAccount";
import MpgLoadingDialog from "../../../../../common/dialog/MpgLoadingDialog";
import { saveAs } from "file-saver";
import MuiLink from "@mui/material/Link";
import { useGetLoggedInUserDetails } from "../../../../../hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import useTrackUserActivity from "../../../../../userActivityTracking/useTrackUserActivity";
import { getGUIDAndExtentionFromFileURL } from "../../../../../hooksAndFunctions/functions/getGUIDAndExtentionFromFileURL";
import { getAccessTypes } from "../../../../../hooksAndFunctions/functions/getAccessTypes";
import { useGetAccountAndSubAccListOnlyForA4Role } from "../../../../../hooksAndFunctions/customHooks/useGetAccountAndSubAccListOnlyForA4Role";
import MpgClientCodeTransferListDialogForClientSubAcc from "../../../../../CommonFormComp/MpgClientCodeTransferListForClientSubAcc/MpgClientCodeTransferListDialogForClientSubAcc";
import {
  mapLangListToAutocomplete,
  mapCurrencyListToAutocomplete,
  mapClientTypeListToAutocomplete,
  setClientCodes,
} from "../../../../../common/utilities/Helper";
import { getClientSubAccountCmsFetchers } from "../clientSubAccountCmsHelper";
import {
  buildAccountLanguages,
  mapAccountTeamMembersWithOrder,
  mapClientAccountAdmins,
  mapClientCodes,
  mapClientUsersWithOrder,
} from "../../accountSubmitTransformHelper";
import {
  useSupportiveLanguageOptions,
  useSyncAccountAdminsValidation,
} from "../../accountFormCommonHooks";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const CreateClientSubAccount = () => {
  const { refClientAccountCmsData } = useOutletContext();
  const {
    fetchClientSubAccountCmsData,
    fetchClientTeamMemberCmsData,
    fetchAccountTeamMemberCmsData,
    fetchAccountAdminCmsData,
  } = getClientSubAccountCmsFetchers(refClientAccountCmsData);
  const { loggedInUser } = useGetLoggedInUserDetails();
  const {
    clientPortalAdminRoleId,
    isEnterpriseAdmin,
    id: userId,
  } = loggedInUser;

  let navigate = useNavigate();

  const userName = JSON.parse(window.sessionStorage.getItem("UserAccount"))?.[
    "name"
  ];

  const { clientPortalAdminType } = getAccessTypes();
  const { hasA2Access, hasA4Access } = clientPortalAdminType;

  /**useful for only A4 role */
  const { clientAccountIdListForA4Role } =
    useGetAccountAndSubAccListOnlyForA4Role({ userId });

  /** !!!!!!!! form handling !!!!!!! */
  const { schema } = createClientSubAccValidation();

  const rhfFormInstance = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const { setValue, watch } = rhfFormInstance;

  const selectedClientAcc = watch("client_acc_name");
  const selectedPrimaryLang = watch("primary_lang");

  const selectedClientAccId = selectedClientAcc?.id;

  /** !!!!!!!! table state handling !!!!!!! */
  const [clientTeamTableData, setClientTeamTableData] = useState([]);
  const [accountTeamMemberTableData, setAccountTeamMemberTableData] =
    React.useState([]);
  const [accountAdminTableData, setAccountAdminTableData] = React.useState([]);

  /** !!!!!!!! dialog state handling !!!!!!! */

  const [openAccountTeamDialog, setOpenAccountTeamDialog] =
    React.useState(false);
  const [openAccountAdminDialog, setOpenAccountAdminDialog] =
    React.useState(false);

  const [openClientAccCodeDialog, setOpenClientAccCodeDialog] = useState(false);
  const [wasAdminEdited, setWasAdminEdited] = useState(false);

  /** !!!!!!!! all API state handling !!!!!!! */
  const {
    mutate: downloadLogo,
    isSuccess: isSuccessOnDownload,
    data: dataOnDownload,
  } = useFileDownloadMutation();

  const {
    isSuccess: isSuccessOnClientCodeList,
    data: clientCodeListData,
    isFetching: isFetchingOnClientCodeList,
  } = useGetClientCodeListByClientAccountIdQuery({
    clientAccountId: !!selectedClientAccId ? selectedClientAccId : "",
  });

  /**get master languagelist */
  const { data: LangList } = useGetLanguageListQuery();

  /**API calls to CreateClientAccount */
  const {
    mutate: createClientSubAccount,
    isError: isErrorOnCreate,
    isLoading: isLoadingOnCreate,
    isSuccess: isSuccessOnCreate,
    error: errorOnCreate,
  } = useCreateClientSubAccountMutation();

  const {
    mutate: uploadLogo,
    isError: isErrorOnLogoUpload,
    isLoading: isLoadingOnLogoUpload,
    isSuccess: isSuccessOnLogoUpload,
    data: dataOnLogoUpload,
  } = useUploadLogoOfClientSubAccountMutation();

  const { data: accountTeamMembersList } = useViewAccountTeamMembersListQuery();

  const { isSuccess: isSuccessOnAccAdminList, data: accountAdminListFromAPI } =
    useGetAccountAdminListQuery();

  const { data: dataOnClientAccountById } = useGetClientAccountByIdQuery({
    clientAccountId:
      !!selectedClientAcc && selectedClientAcc.id ? selectedClientAcc.id : "",
  });

  const { data: CurrencyList } = useGetCurrencyListQuery();

  const { data: ClientTypeList } = useGetClientTypeClassificationQuery();

  /**get list of all client accounts */
  const { mutate: getClientAccountList, data: clientAccountList } =
    useGetClientAccountDropdownMutation();

  useEffect(() => {
    getClientAccountList({
      loggedInUserId: userId,
      roleId: clientPortalAdminRoleId,
      isEnterpriseAdmin,
    });
  }, [loggedInUser]);

  const clientAccListFromAPI = clientAccountList?.data;

  const { data: langListByClientAccountOrClientSubAccountIdData } =
    useGetLanguageListByClientAccountOrClientSubAccountId({
      ClientAccountOrClientSubAccountId:
        !!selectedClientAcc && selectedClientAcc.id ? selectedClientAcc.id : "",
    });

  /** !!!!!!!! autocomplete option lists !!!!!!! */
  const primaryLangAutocompleteList = mapLangListToAutocomplete(LangList);

  const currencyAutocompleteList = mapCurrencyListToAutocomplete(CurrencyList);

  const ClientTypeAutocompleteList =
    mapClientTypeListToAutocomplete(ClientTypeList);

  const supportiveLangAutocompleteList = useSupportiveLanguageOptions({
    LangList,
    selectedPrimaryLang,
  });

  useSyncAccountAdminsValidation({
    rhfFormInstance,
    accountAdminTableData,
  });

  const clientAccAutocompleteList =
    !!clientAccListFromAPI &&
    clientAccListFromAPI
      /**for create only passing only active accounts */
      .filter((item) => !!item.IsActive)
      .map((item) => {
        const disableOptionForA4Role = !clientAccountIdListForA4Role.includes(
          item?.Id
        );

        let disableOption = false;
        /**(only for A2/A4 role) get all clinet sub acc ids where this user is not A4 admin and diable those autocomplete options */
        if ((!!hasA4Access || !!hasA2Access) && !!disableOptionForA4Role) {
          disableOption = true;
        } else {
          /**for rest of roles */
          disableOption = false;
        }
        return {
          id: item.Id,
          selectOptionLabel: `${item.ClientAccountName}`,
          selectOptionValue: item.Id,
          selectOptionDisabled: !!disableOption,
          extraData: item,
        };
      });

  /** !!!!!!!! compute primary and supportive langs !!!!!!! */
  /**clearing prim and supportive langs if client acc is not selected */
  useEffect(() => {
    if (!selectedClientAcc) {
      setValue("primary_lang", "");
      setValue("supported_lang", []);
    }
  }, [selectedClientAcc]);

  /**get languages values */

  /**these are existing langs from client account */
  const [existingPrimLang, setExistingPrimLang] = useState({});
  const [existingSupportedLang, setExistingSupportedLang] = useState([]);

  useEffect(() => {
    if (
      !!langListByClientAccountOrClientSubAccountIdData &&
      !!langListByClientAccountOrClientSubAccountIdData.length
    ) {
      let prim = {};
      let supportive = [];

      langListByClientAccountOrClientSubAccountIdData.forEach((eachLang) => {
        const foundLang =
          !!LangList &&
          LangList.find((item) => item.Id === eachLang.LanguageID);

        if (!!foundLang) {
          if (!!eachLang.IsPrimary) {
            prim = foundLang;
          } else {
            supportive.push(foundLang);
          }
        }
      });
      setExistingPrimLang(prim);
      setExistingSupportedLang(supportive);
    } else {
      setExistingPrimLang([]);
      setExistingSupportedLang([]);
    }
  }, [langListByClientAccountOrClientSubAccountIdData, LangList]);

  /**setting prim lang default*/
  useEffect(() => {
    if (!!existingPrimLang.Id) {
      const existingPrimLangDefaultList = {
        id: existingPrimLang.Id,
        selectOptionLabel: `${existingPrimLang.LanguageCode}-${existingPrimLang.LanguageDescription}`,
        selectOptionValue: existingPrimLang.Id,
      };
      setValue("primary_lang", existingPrimLangDefaultList || "", {
        shouldValidate: true,
      });
    }
  }, [existingPrimLang]);

  /**setting supported lang default*/
  useEffect(() => {
    if (!!existingSupportedLang.length) {
      const existingSupportedLangDefaultList =
        !!existingSupportedLang &&
        existingSupportedLang.map((item) => {
          return {
            id: item.Id,
            selectOptionLabel: `${item.LanguageCode}-${item.LanguageDescription}`,
            selectOptionValue: item.Id,
          };
        });
      setValue("supported_lang", existingSupportedLangDefaultList || [], {
        shouldValidate: true,
      });
    }
  }, [existingSupportedLang]);

  /** !!!!!!!!! filtering Admin list on Permissions !!!!!!!!!! */
  /**for client sub-accounts, admins with only A1 A2 and A4 permisions allowed */
  const [filteredAdminList, setFilteredAdminList] = useState([]);
  useEffect(() => {
    if (!!isSuccessOnAccAdminList && !!accountAdminListFromAPI) {
      let newList = [];
      const regExp = /A1|A2|A4|Enterprise Admin/;
      accountAdminListFromAPI.forEach((item) => {
        if (item?.permissionRole.search(regExp) !== -1) {
          newList.push(item);
        }
      });
      setFilteredAdminList(newList);
    }
  }, [accountAdminListFromAPI, isSuccessOnAccAdminList]);

  /** !!!!!!! grabing data from client code transfer list !!!!!! */
  const [finalCodeList, setFinalCodeList] = useState([]);
  useEffect(() => {
    setClientCodes(finalCodeList, setValue);
  }, [finalCodeList, setValue]);

  /**on change in client account ID, previously selected client codes should be cleared */
  const [myClientAccId, setMyClientAccId] = useState("");
  useEffect(() => {
    setMyClientAccId((prevVal) => {
      if (prevVal !== selectedClientAccId) {
        /**IMP: action to clear client code data in text box and dialog*/
        setFinalCodeList([]);

        /**IMP: change in client account id, reset removeLogo state to false. */
        setRemoveLogo(false);

        /**IMP: change inheritated logo as change in client account */
        setFileDetails({});
      }
      return selectedClientAccId;
    });
  }, [selectedClientAccId]);

  /** !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! */
  /** !!!!!!! populating existing data in tables on the basis of selected client account !!!!!! */
  useEffect(() => {
    if (!!dataOnClientAccountById) {
      /** */
      const existingClientUsers = dataOnClientAccountById?.ClientUserTeams;
      if (!!existingClientUsers) {
        const list = [];
        /**allow only CL1 | CL2 | CL9 client users */
        const regExp = /CL1|CL2|CL9/;
        existingClientUsers.forEach((item) => {
          if (item?.PermissionCode.search(regExp) !== -1) {
            list.push(item);
          }
        });
        setClientTeamTableData(list);
      }

      /** */
      const existingAccTeamMemberList =
        dataOnClientAccountById?.AccountUserTeams;
      if (!!existingAccTeamMemberList) {
        let list = [];
        existingAccTeamMemberList &&
          existingAccTeamMemberList.forEach((item) => {
            list.push({
              teamMemberId: item.StaffId,
              name: `${item.FirstName} ${item.LastName}`,
              email: item.EmailAddress,
              contact: item.ContactNo,
              jobPosition: item.JobPosition,
              bioId: item.BioId,
              bioTitle: item.TemplateTitle,
            });
          });
        setAccountTeamMemberTableData(list);
      }
    }
  }, [dataOnClientAccountById]);

  useEffect(() => {
    if (!!dataOnClientAccountById) {
      const existingClientadmins = dataOnClientAccountById?.ClientAccountAdmins;

      let requiredAdminList = [];

      existingClientadmins.forEach((eachAdmin) => {
        const foundItem =
          !!accountAdminListFromAPI &&
          accountAdminListFromAPI.find(
            (item) => item.adminId === eachAdmin.adminId
          );

        if (!!foundItem) {
          requiredAdminList.push(foundItem);
        }
      });
      setAccountAdminTableData(requiredAdminList);
    }
  }, [dataOnClientAccountById, accountAdminListFromAPI]);

  useEffect(() => {
    /**onchange in client account clear old values in tables */
    if (!selectedClientAcc) {
      setClientTeamTableData([]);
      setAccountTeamMemberTableData([]);
      setAccountAdminTableData([]);
    }
  }, [dataOnClientAccountById, selectedClientAcc]);

  /**handling and processing for file download */
  const [fileDetails, setFileDetails] = useState({});
  /**{
   *  fileGUID:"dsdsdsdsdsdd",
   * fileExtension:"png",
   * } */
  useEffect(() => {
    if (!!dataOnClientAccountById?.Logo) {
      const file = dataOnClientAccountById?.Logo;

      const splitFileURL = file.split("/");

      const fileGUIDWithExtension = splitFileURL.at(-1);

      const fileGUIDWithExtensionSplit = fileGUIDWithExtension.split(".");

      const fileGUID = fileGUIDWithExtensionSplit[0];
      const fileExtension = fileGUIDWithExtensionSplit[1];

      setFileDetails({
        fileGUID,
        fileExtension,
      });
    } else {
      setFileDetails({});
    }
  }, [dataOnClientAccountById?.Logo]);

  useEffect(() => {
    if (!!isSuccessOnDownload) {
      saveAs(
        dataOnDownload,
        `${dataOnClientAccountById?.ClientAccountName}_logo`
      );
    }
  }, [isSuccessOnDownload]);

  /**remove logo */
  const [removeLogo, setRemoveLogo] = useState(false);

  /** !!!!!!!! submit handling !!!!!!! */
  const [dataForCreate, setDataForCreate] = useState({});
  const onSubmit = (data) => {
    const langList = buildAccountLanguages({
      primaryLangId: data.primary_lang?.id,
      supportedLangs: data.supported_lang || [],
    });

    const codeList = mapClientCodes({
      finalCodeList,
      codeKey: "clientCode",
      codeNameKey: "clientCodeName",
    });

    const clientUserList = mapClientUsersWithOrder(clientTeamTableData || []);

    const accountTeamMembList = mapAccountTeamMembersWithOrder(
      accountTeamMemberTableData || []
    );

    const clientAccAdminList = mapClientAccountAdmins(accountAdminTableData || []);

    const body = {
      UserId: userId,
      ParentID: data?.client_acc_name?.extraData?.Id,
      ClientAccountName: data?.client_sub_acc_name || "",
      /**if logo is not uploaded for sub-accound then add logo from its parent client account */
      Logo: !!dataOnClientAccountById?.Logo
        ? dataOnClientAccountById?.Logo
        : "",
      IsActive: true,
      CreatedBy: userName || "",
      CurrencyId: data.CurrencyId?.id,
      DataClassificationKey: data.ClientType?.id,
      AccountLanguages: langList || [],
      ClientUserTeams: clientUserList || [],
      AccountUserTeams: accountTeamMembList || [],
      ClientAccountAdmins: clientAccAdminList || [],
      ClientAccountCodes: codeList || [],
    };
    setDataForCreate(body);

    // console.log("gl-subm", body)

    if (!!data.logo_img[0]) {
      const file = data.logo_img[0];
      uploadLogo({ file });
    } else {
      createClientSubAccount({ body });
    }

    // console.log("subForm", data)
  };

  useEffect(() => {
    if (!!isSuccessOnLogoUpload) {
      const storedFileURL = dataOnLogoUpload?.data?.FileUrl;
      const bodyData = {
        ...dataForCreate,
        Logo: storedFileURL,
      };
      createClientSubAccount({ body: bodyData });
    }
  }, [isSuccessOnLogoUpload]);

  useEffect(() => {
    if (!!isSuccessOnCreate) {
      navigate(clientSubAccountViewPath);
    }
  }, [isSuccessOnCreate]);

  /**tracking user Activity */
  const { trackActivity } = useTrackUserActivity();
  useEffect(() => {
    if (!!isSuccessOnLogoUpload) {
      const { fileGUID } = getGUIDAndExtentionFromFileURL({
        fileURL: dataOnLogoUpload?.data?.FileUrl,
      });

      trackActivity({
        activitySuccess: !!isSuccessOnLogoUpload,
        actionType: "Uploaded",
        itemId: fileGUID || "",
      });
    }
  }, [isSuccessOnLogoUpload]);

  /**handling error states and msgs */
  const [errorTitle, setErrorTitle] = useState("");
  const [errorContent, setErrorContent] = useState("");

  //   const handleDeleteAdmin = (adminId) => {
  //     // Remove selected admin from table data
  //     const updatedAdmins = accountAdminTableData.filter(
  //       (admin) => admin.adminId !== adminId
  //     );
  //     setAccountAdminTableData(updatedAdmins);

  //     // Sync RHF field and trigger validation to show error if empty
  //     rhfFormInstance.setValue("accountAdmins", updatedAdmins, {
  //       shouldValidate: true,
  //     });
  //     rhfFormInstance.trigger("accountAdmins");
  //   };

  useEffect(() => {
    if (
      !!isErrorOnCreate &&
      errorOnCreate?.response?.data === "NoAccessForA4Role"
    ) {
      setErrorTitle(
        fetchClientSubAccountCmsData(
          "ClientSubAccountDetails.WarningMsgUnauthorized"
        )
      );
      setErrorContent(
        fetchClientSubAccountCmsData(
          "ClientSubAccountDetails.ValidationMsgUnauthorized"
        )
      );
    } else if (
      !!isErrorOnCreate &&
      errorOnCreate?.response?.data === "AccountNameExists"
    ) {
      setErrorTitle("Sub-Account Name exists. Try again...!");
      setErrorContent(
        fetchClientSubAccountCmsData(
          "ClientSubAccountDetails.ValidationMsgDuplicateSubAccountName"
        ).replace("#CLIENT_ACCOUNT_NAME#", `"${watch("client_sub_acc_name")}"`)
      );
    } else {
      setErrorTitle("");
      setErrorContent("");
    }
  }, [isErrorOnCreate, errorOnCreate]);

  return (
    <Box sx={{ width: "100%" }}>
      {/* <CommonButtonHeader /> */}
      <Box sx={{ width: "100%" }}>
        <Typography variant="h5" align="center" sx={{ fontWeight: "bolder" }}>
          {refClientAccountCmsData?.Components &&
            refClientAccountCmsData?.Components[
              "ClientSubAccountDetails.SetupClientSubAccount"
            ]?.Title}
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          m={1}
        >
          <Button
            variant="outlined"
            size="small"
            component={Link}
            to={clientSubAccountViewPath}
          >
            {fetchClientSubAccountCmsData(
              "ClientSubAccountDetails.ClientSubAccounts"
            )}
          </Button>

          <Button variant="outlined" size="small" disabled>
            {fetchClientSubAccountCmsData(
              "ClientSubAccountDetails.ViewAllUser"
            )}
          </Button>
        </Stack>

        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={rhfFormInstance.handleSubmit(onSubmit)}
        >
          <Item>
            <Typography variant="h6" align="left" sx={{ fontWeight: "bolder" }}>
              {fetchClientSubAccountCmsData(
                "ClientSubAccountDetails.ClientAccountDetails"
              )}
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"client_sub_acc_name"}
                  label={fetchClientSubAccountCmsData(
                    "ClientSubAccountDetails.SubAccountGroupName"
                  )}
                  fullWidth
                />

                <MpgAutocompleteSingle
                  rhfFormInstance={rhfFormInstance}
                  name={"client_acc_name"}
                  label={fetchClientSubAccountCmsData(
                    "ClientSubAccountDetails.ClientAccountName"
                  )}
                  fullWidth
                  optionList={clientAccAutocompleteList || []}
                />

                <MpgAutocompleteSingle
                  rhfFormInstance={rhfFormInstance}
                  name={"primary_lang"}
                  label={fetchClientSubAccountCmsData(
                    "ClientSubAccountDetails.PrimaryLanguages"
                  )}
                  fullWidth
                  optionList={primaryLangAutocompleteList || []}
                />
                <MpgAutocompleteMulti
                  rhfFormInstance={rhfFormInstance}
                  name={"supported_lang"}
                  label={fetchClientSubAccountCmsData(
                    "ClientSubAccountDetails.SupportedLanguages"
                  )}
                  fullWidth
                  optionList={supportiveLangAutocompleteList || []}
                  disabled={!selectedPrimaryLang?.id}
                />
                <MpgAutocompleteSingle
                  rhfFormInstance={rhfFormInstance}
                  name={"CurrencyId"}
                  label={fetchClientSubAccountCmsData(
                    "ClientSubAccountDetails.Currency"
                  )}
                  fullWidth
                  optionList={currencyAutocompleteList || []}
                />
                <MpgAutocompleteSingle
                  rhfFormInstance={rhfFormInstance}
                  name={"ClientType"}
                  label={"Type"}
                  fullWidth
                  optionList={ClientTypeAutocompleteList || []}
                />

                <Box>
                  <MpgFileUpload
                    name={"logo_img"}
                    label={fetchClientSubAccountCmsData(
                      "ClientSubAccountDetails.Logo"
                    )}
                    rhfFormInstance={rhfFormInstance}
                  />
                  {/** file download func */}
                  {!removeLogo && !!fileDetails?.fileGUID && (
                    <Grid>
                      <Tooltip title="Remove Logo">
                        <IconButton
                          aria-label="delete"
                          onClick={() => {
                            setRemoveLogo(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      <MuiLink
                        onClick={() => {
                          if (!!fileDetails?.fileGUID) {
                            downloadLogo({
                              fileGUID: fileDetails?.fileGUID,

                              /**DownloadType: "profileImage"|"logo"|"reportAndResources" */
                              DownloadType: "logo",
                              Extension: fileDetails?.fileExtension,
                            });
                          }
                        }}
                      >
                        {`${dataOnClientAccountById?.ClientAccountName}_logo (inherited)`}
                      </MuiLink>
                    </Grid>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <div>
                  <MpgInput
                    rhfFormInstance={rhfFormInstance}
                    label={fetchClientSubAccountCmsData(
                      "ClientSubAccountDetails.SelectedClientCodes"
                    )}
                    name={"client_codes"}
                    multiline
                    rows={10}
                    fullWidth
                    disabled={true}
                  />
                  {!!isFetchingOnClientCodeList && (
                    <Button disabled>
                      <CircularProgress size={15} sx={{ margin: "5px" }} />
                      {fetchClientSubAccountCmsData(
                        "ClientSubAccountDetails.Fetch"
                      )}
                    </Button>
                  )}
                  {!!isSuccessOnClientCodeList && !!selectedClientAccId && (
                    <Button
                      onClick={() => {
                        setOpenClientAccCodeDialog(true);
                      }}
                    >
                      {fetchClientSubAccountCmsData(
                        "ClientSubAccountDetails.SelectClientCodes"
                      )}
                    </Button>
                  )}
                </div>
              </Grid>
            </Grid>
          </Item>
          <br />
          <Item>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="h6"
                align="center"
                sx={{ fontWeight: "bolder" }}
              >
                {refClientAccountCmsData?.Components &&
                  refClientAccountCmsData?.Components[
                    "ClientAccounts.ClientTeamMembers"
                  ]?.Title}
              </Typography>
              <Button variant="outlined" size="small" disabled>
                {fetchClientTeamMemberCmsData("ClientTeamMembers.Select")}
              </Button>
            </Stack>
            <ClientTeamTable tableData={clientTeamTableData || []} />
          </Item>
          <br />
          <Item>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="h6"
                align="center"
                sx={{ fontWeight: "bolder" }}
              >
                {refClientAccountCmsData?.Components &&
                  refClientAccountCmsData?.Components[
                    "ClientAccounts.AccountTeamMembers"
                  ]?.Title}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setOpenAccountTeamDialog(true);
                }}
              >
                {fetchAccountTeamMemberCmsData("AccountTeamMembers.Select")}
              </Button>
            </Stack>
            <AccountTeamTable
              tableData={accountTeamMemberTableData || []}
              setTableData={setAccountTeamMemberTableData}
              setWasAdminEdited={setWasAdminEdited}
            />
          </Item>
          <br />
          <Item
            sx={{
              border:
                rhfFormInstance.formState.errors.accountAdmins &&
                (rhfFormInstance.formState.isSubmitted|| wasAdminEdited)
                  ? "1px solid red"
                  : undefined,
              borderRadius: 1,
              pb: 1,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="h6"
                align="center"
                sx={{ fontWeight: "bolder" }}
              >
                {refClientAccountCmsData?.Components &&
                  refClientAccountCmsData?.Components[
                    "ClientAccounts.AccountAdmins"
                  ]?.Title}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setOpenAccountAdminDialog(true);
                }}
              >
                {fetchAccountAdminCmsData("AccountAdmins.Select")}
              </Button>
            </Stack>

            <AccountAdminTable
              tableData={accountAdminTableData || []}
              setTableData={setAccountAdminTableData}
              rhfFormInstance={rhfFormInstance}
              setWasAdminEdited={setWasAdminEdited}
            />

            {rhfFormInstance.formState.errors.accountAdmins &&
              (rhfFormInstance.formState.isSubmitted || wasAdminEdited) && (
                <Typography
                  color="error"
                  variant="caption"
                  align="left"
                  sx={{ px: 2, pt: 1 }}
                >
                  {rhfFormInstance.formState.errors.accountAdmins.message}
                </Typography>
              )}
          </Item>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent={"space-evenly"}
            mt={3}
          >
            <Button variant="contained" size="small" type="submit">
              {fetchClientSubAccountCmsData(
                "ClientSubAccountDetails.CreateClientSubAccount"
              )}
            </Button>
          </Stack>
        </Box>
      </Box>

      <AccountTeamMemberDialog
        open={openAccountTeamDialog}
        setOpen={setOpenAccountTeamDialog}
        accountTeamMemberTableData={accountTeamMemberTableData || []}
        setAccountTeamMemberTableData={setAccountTeamMemberTableData}
        accountTeamMembersListFromAPI={accountTeamMembersList || []}
      />
      <AccountAdminsDialog
        open={openAccountAdminDialog}
        setOpen={setOpenAccountAdminDialog}
        accountAdminTableData={accountAdminTableData || []}
        setAccountAdminTableData={setAccountAdminTableData}
        accountAdminListFromAPI={filteredAdminList || []}
      />

      <MpgClientCodeTransferListDialogForClientSubAcc
        transferDialogTitle={fetchClientSubAccountCmsData(
          "ClientSubAccountDetails.SelectClientCodes"
        )}
        leftTableTitle={fetchClientSubAccountCmsData(
          "ClientSubAccountDetails.AvailableClientCodes"
        )}
        rightTableTitle={fetchClientSubAccountCmsData(
          "ClientSubAccountDetails.SelectedClientCodes"
        )}
        open={openClientAccCodeDialog}
        setOpen={setOpenClientAccCodeDialog}
        grabRightTableData={setFinalCodeList}
        leftTableDataToPop={clientCodeListData || []}
        rightTableDataToPop={finalCodeList || []}
      />

      {/** error dialog on create action */}
      <MpgErrorDialog
        open={!!isErrorOnCreate || !!isErrorOnLogoUpload}
        title={errorTitle || ""}
        content={errorContent || ""}
      />
      <MpgLoadingDialog open={!!isLoadingOnCreate || !!isLoadingOnLogoUpload} />
    </Box>
  );
};

export default CreateClientSubAccount;
